import {
    useApolloClient,
    useLazyQuery,
    useMutation,
    useQuery,
} from '@apollo/client'
import { AuthContext } from '@deltek/specification-client/core/Configurations/AuthContext'
import { Admin } from '@deltek/specification-client/core/Configurations/Authorization'
import {
    getDesignatedAdminRoutingComponent,
    GetDesignatedAdminRoutingComponentProps,
} from '@deltek/specification-client/core/Configurations/DesignatedAdminRouting'
import {
    GlobalSnackbar,
    GlobalSnackbarPropsQuery,
    GlobalSnackbarPropsQueryResponse,
    HideGlobalSnackbarMutation,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import ContentContainer from '@deltek/specification-client/core/Containers/ContentContainer'
import { AppBarContext } from '@deltek/specification-client/core/Contexts/AppBarContext'
import {
    ILoadingPageContext,
    LoadingPageContext,
} from '@deltek/specification-client/core/Contexts/LoadingPageContext'
import {
    CurrentUserClaimInfoQuery,
    GetReleaseNoteUriQuery,
} from '@deltek/specification-client/core/GraphQL/Queries/currentUserInfo.queries'
import AuthEventService from 'shared/services/authEventService'

import ResponsiveMenus from '@deltek/specification-client/core/Menus/ResponsiveMenus'
import {
    CurrentUserFirmsProps,
    CurrentUserInfo,
    CurrentUserInfoResponse,
    MasterSpecNoteUriResponse,
    UserNotification,
} from '@deltek/specification-client/core/Models/currentUserInfo'
import { Firm, FirmId } from '@deltek/specification-client/core/Models/firm'
import { MenuItemConfig } from '@deltek/specification-client/core/Models/menuItemConfig'
import { FirmUserResponse } from '@deltek/specification-client/core/Models/user'
import {
    createProductionOnlineHelpUrl,
    createTestingOnlineHelpUrl,
    navigateToExternalLink,
} from '@deltek/specification-client/core/Utilities/urlUtils'
import {
    AddUserTokenToBlacklistMutation,
    SetUserLastActiveDateMutation,
} from 'api/graphQL/mutations/user.mutations'
import {
    FirmDesignatedAccountAdminQuery,
    FirmSupportContactUserQuery,
    GetFirmGainsightAggregatesQuery,
} from 'api/graphQL/queries/firm.queries'
import { UserProfileQuery } from 'api/graphQL/queries/userProfile.queries'
import {
    setIsBestPracticeEnabled,
    setUser,
} from 'api/graphQL/reactiveVars/user'
import {
    isContentTeam,
    isOmWorkspaceUser,
} from 'api/graphQL/reactiveVars/workspaceContent'
import {
    FirmGainsightAggregatesResponse,
    FirmResponse,
} from 'api/graphQL/types/firm.types'
import AppHeader from 'components/AppHeader/AppHeader'
import { useChatProfileStore } from 'components/AskDelaChat/ChatProfileStore'
import {
    isProduction,
    isStaging,
    processEnv,
} from 'components/configuration/environmentVariables'
import UserProfileUpdate from 'components/UserProfileUpdate'
import MainPageRouting from 'pages/MainPage/MainPageRouting'
import React, { useContext, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import {
    ProductListings,
    ProjectNotes,
    SectionPreview,
} from 'shared/browserTitle'
import { GAINSIGHT_APP_AE } from 'shared/utils/gainsight'
import { useGainsightIdentify } from 'shared/utils/useGainsightIdentify'
const isUserDesignatedAccountAdmin = (
    currentUserInfo: CurrentUserInfo,
    firm: Partial<Firm>
) =>
    firm?.designatedAccountAdminUser?.email?.toLowerCase() ===
    currentUserInfo?.email?.toLowerCase()

const getHelpDeskEmailAddress = (
    currentUserInfo: CurrentUserInfo,
    firm: Partial<Firm>
) => {
    let supportContactEmail = 'SpecificationSupport@Deltek.com'

    if (!isUserDesignatedAccountAdmin(currentUserInfo, firm))
        supportContactEmail = firm?.designatedAccountAdminUser?.email!

    return supportContactEmail
}

const aeReleaseNotesUrl =
    'https://dsm.deltek.com/DeltekSoftwareManagerWebServices/downloadFile.ashx?documentid=AC1BCD30-65D7-4BAF-B67F-59C08CBB5652'

const aeAutodeskRevitPlugins =
    'https://dsm.deltek.com/files/products/Specpoint/DeltekSpecpointRevitPlugins.zip'

const MainPage: React.FC = () => {
    const { logOut, changePassword } = useContext(AuthContext)

    const setChatUser = useChatProfileStore((state) => state.setChatUser)

    const [
        getFirmDesignatedAdminUserId,
        {
            called: firmDesignatedAdminCalled,
            loading: firmDesignatedAdminLoading,
            data: firmDesignatedAdminResponse,
        },
    ] = useLazyQuery<FirmResponse>(FirmDesignatedAccountAdminQuery)

    const location = useLocation()

    const [getResponse, { data: currentUserData }] = useLazyQuery<
        FirmUserResponse
    >(UserProfileQuery, {
        onCompleted: (data) => {
            setIsBestPracticeEnabled(
                data?.user?.isBestPracticeGroupEnabled ?? false
            )
            setChatUser(data?.user)
        },
    })

    const [
        getFirmResponse,
        { data: firmSupportContactData, loading: firmSupportContactLoading },
    ] = useLazyQuery<FirmResponse, FirmId>(FirmSupportContactUserQuery)

    const [
        getFirmGainsightAggregates,
        { data: firmGainsightAggregatesData },
    ] = useLazyQuery<FirmGainsightAggregatesResponse, FirmId>(
        GetFirmGainsightAggregatesQuery
    )

    const [
        getMasterSpecReleaseNoteUri,
    ] = useLazyQuery<MasterSpecNoteUriResponse>(GetReleaseNoteUriQuery, {
        variables: {
            input: {
                releaseNoteName: 'MASTERSPEC',
            },
        },
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            const uri = data?.getReleaseNoteUri
            if (uri) {
                navigateToExternalLink?.(uri)
            }
        },
        onError: (error) => {
            console.error('Error fetching MasterSpec release notes:', error)
        },
    })

    const [
        getSpectextReleaseNoteUri,
    ] = useLazyQuery<MasterSpecNoteUriResponse>(GetReleaseNoteUriQuery, {
        variables: {
            input: {
                releaseNoteName: 'SPECTEXT',
            },
        },
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            const uri = data?.getReleaseNoteUri
            if (uri) {
                navigateToExternalLink?.(uri)
            }
        },
        onError: (error) => {
            console.error('Error fetching Spectext release notes:', error)
        },
    })

    const { data: currentUserInfoResponse } = useQuery<CurrentUserInfoResponse>(
        CurrentUserClaimInfoQuery,
        {
            fetchPolicy: 'network-only',
            onCompleted: (data: CurrentUserInfoResponse) => {
                setUser(data.currentUserInfo)
                getResponse({
                    variables: {
                        firmId: data?.currentUserInfo?.account?.accountId ?? '',
                        userId: data?.currentUserInfo?.kpUserId ?? '',
                    },
                })

                getFirmResponse({
                    variables: {
                        firmId: data?.currentUserInfo?.account?.accountId ?? '',
                    },
                })

                getFirmGainsightAggregates({
                    variables: {
                        firmId: data?.currentUserInfo?.account?.accountId ?? '',
                    },
                })
            },
        }
    )

    const { data: globalSnackbarPropsData } = useQuery<
        GlobalSnackbarPropsQueryResponse
    >(GlobalSnackbarPropsQuery)

    useGainsightIdentify({
        currentUserInfo: currentUserInfoResponse?.currentUserInfo,
        firmSupportContactData,
        firmGainsightAggregatesData,
        currentUserData,
        app: GAINSIGHT_APP_AE,
    })

    // Handlers for fetching release notes on demand
    const handleMasterSpecUpdatesClick = React.useCallback(() => {
        getMasterSpecReleaseNoteUri()
    }, [getMasterSpecReleaseNoteUri])

    const handleSpectextUpdatesClick = React.useCallback(() => {
        getSpectextReleaseNoteUri()
    }, [getSpectextReleaseNoteUri])

    const [hideGlobalSnackbar] = useMutation(HideGlobalSnackbarMutation)

    const [userProfileClicked, setUserProfileClicked] = React.useState<boolean>(
        false
    )

    const [userFirmDetail, setUserFirmDetail] = React.useState<
        CurrentUserFirmsProps
    >()

    const {
        customContent,
        actionBarVisible,
        customActionBar,
        currentPageTitle,
        customPartialNavBarMenuComponent,
        customMenuItems,
        headerVisible,
        setHeaderVisibility,
        currentUserActiveFirm,
    } = React.useContext(AppBarContext)

    const [appHeaderHeight, setAppHeaderHeight] = React.useState(50)

    const { handleLoadingPageChange, showLoadingScreenPage } = useContext<
        ILoadingPageContext
    >(LoadingPageContext)

    const { search } = useLocation()
    const locSearch = React.useMemo(() => new URLSearchParams(search), [search])
    const client = useApolloClient()

    const onUserProfileClick = React.useCallback(() => {
        setUserProfileClicked(true)
    }, [setUserProfileClicked])

    useEffect(() => {
        const isNewUser = locSearch.get('newuser') === '1'
        if (onUserProfileClick && isNewUser) {
            onUserProfileClick()
        }
    }, [locSearch, onUserProfileClick])

    const history = useHistory()
    const handleDialogClose = () => {
        const searchParams = new URLSearchParams(location.search)
        if (searchParams.has('newuser')) {
            searchParams.delete('newuser')
            history.replace({
                ...location,
                search: `?${searchParams.toString()}`,
            })
        }
        setUserProfileClicked(false)
    }

    const [setUserLastActive] = useMutation(SetUserLastActiveDateMutation)
    const [addUserTokenToBlacklist] = useMutation(
        AddUserTokenToBlacklistMutation
    )

    const helpDeskEmailAddress: string = React.useMemo(() => {
        if (!firmSupportContactLoading && firmSupportContactData) {
            return getHelpDeskEmailAddress(
                currentUserInfoResponse?.currentUserInfo!,
                firmSupportContactData?.firm
            )
        }
        return ''
    }, [
        firmSupportContactLoading,
        firmSupportContactData,
        currentUserInfoResponse,
    ])

    const userIsDesignatedSupportPerson: boolean = React.useMemo(() => {
        return isUserDesignatedAccountAdmin(
            currentUserInfoResponse?.currentUserInfo!,
            firmSupportContactData?.firm!
        )
    }, [currentUserInfoResponse, firmSupportContactData])

    const onlineHelpUrl: string = React.useMemo(() => {
        let userAccountType =
            currentUserInfoResponse?.currentUserInfo?.account.accountType ?? ''
        if (
            userAccountType === 'System' &&
            currentUserActiveFirm?.firmAccountType === 'AEFirm'
        ) {
            userAccountType = 'AEFirm'
        }
        return isProduction || isStaging
            ? createProductionOnlineHelpUrl(userAccountType)
            : createTestingOnlineHelpUrl(userAccountType)
    }, [
        createProductionOnlineHelpUrl,
        createTestingOnlineHelpUrl,
        currentUserActiveFirm,
        currentUserInfoResponse,
    ])

    const activeFirmRole: string = React.useMemo(() => {
        return currentUserActiveFirm?.firmRole ?? ''
    }, [currentUserActiveFirm])

    const activeFirmIsPrimary: boolean | undefined = React.useMemo(() => {
        return currentUserActiveFirm?.isPrimary
    }, [currentUserActiveFirm])

    const helpMenuItems: MenuItemConfig[] = React.useMemo(
        () => [
            {
                dataTestId: 'globalHelpMenuItemOnlineHelp',
                label: 'Online Help',
                onClick: () => navigateToExternalLink?.(onlineHelpUrl),
            },
            {
                dataTestId: 'globalHelpMenuItemReleaseNotes',
                label: 'Release Notes',
                onClick: () => navigateToExternalLink?.(aeReleaseNotesUrl),
            },
            {
                dataTestId: 'globalMasterSpecReleaseNotes',
                label: 'MasterSpec Updates',
                onClick: handleMasterSpecUpdatesClick,
            },
            {
                dataTestId: 'globalSpectextReleaseNotes',
                label: 'Spectext Updates',
                onClick: handleSpectextUpdatesClick,
            },
            {
                dataTestId: 'globalAutodeskRevitPlugins',
                label: 'Autodesk Revit\xAE Plugins',
                onClick: () => navigateToExternalLink?.(aeAutodeskRevitPlugins),
            },
            ...(activeFirmRole === 'Admin' ||
                (activeFirmRole === 'Reviewer' && activeFirmIsPrimary) ||
                (activeFirmRole === 'Author' && activeFirmIsPrimary)
                ? [
                    {
                        dataTestId: 'globalHelpMenuItemEmailHelpDesk',
                        label: 'Email Help Desk',
                        onClick: () =>
                            navigateToExternalLink?.(
                                `mailto:${helpDeskEmailAddress}`,
                                true
                            ),
                    },
                ]
                : []),
            ...(userIsDesignatedSupportPerson
                ? [
                    {
                        dataTestId: 'globalHelpMenuItemDesktopSupportCenter',
                        label: 'Deltek Support Center',
                        onClick: () =>
                            navigateToExternalLink?.(
                                'https://deltek.custhelp.com'
                            ),
                    },
                ]
                : []),
            {
                label: 'Privacy Policy',
                dataTestId: 'globalHelpMenuItemPrivacyPolicy',
                onClick: () =>
                    navigateToExternalLink?.(
                        'https://www.deltek.com/en/deltek-contracts/is-user-privacy-policy'
                    ),
            },
            {
                label: 'Terms of Use',
                dataTestId: 'globalHelpMenuItemTermsOfUse',
                onClick: () =>
                    navigateToExternalLink?.(
                        'https://www.deltek.com/en/deltek-contracts/masterspec'
                    ),
            },
        ],
        [
            helpDeskEmailAddress,
            userIsDesignatedSupportPerson,
            onlineHelpUrl,
            handleMasterSpecUpdatesClick,
            handleSpectextUpdatesClick,
            activeFirmRole,
            activeFirmIsPrimary,
        ]
    )

    const kpcUserMenuItems: MenuItemConfig[] = React.useMemo(
        () => [
            ...(activeFirmRole === 'Admin'
                ? [
                    {
                        label: 'Account',
                        dataTestId: 'userDropdownItemAccounts',
                        onClick: () => history.push('/account'),
                    },
                    {
                        label: 'Users',
                        dataTestId: 'userDropdownItemUsers',
                        onClick: () => history.push('/users'),
                    },
                ]
                : []),
        ],
        [history, activeFirmRole]
    )
    const handleRelatedProductsBanner = React.useCallback(() => {
        const localStorageKeys = Object.keys(localStorage)
        const relatedProductsKeys = localStorageKeys?.filter((key) =>
            key.includes(
                `RelatedProductsBanner-${currentUserInfoResponse?.currentUserInfo?.kpUserId}`
            )
        )
        relatedProductsKeys?.forEach((bannerKey) =>
            localStorage.removeItem(bannerKey)
        )
    }, [currentUserInfoResponse])

    const handleLogout = React.useCallback(async () => {
        // Emit logout event to notify services (e.g., clear preview features cache)
        // This must happen BEFORE logout to ensure all listeners can clean up
        AuthEventService.emitLogout()

        await addUserTokenToBlacklist()
        logOut()
        handleRelatedProductsBanner()
        sessionStorage.removeItem('sp_bpProjectId')
    }, [logOut, currentUserInfoResponse, addUserTokenToBlacklist])

    const commonUserMenuItems: MenuItemConfig[] = React.useMemo(
        () => [
            {
                label: 'My Profile',
                dataTestId: 'userDropdownItemMyProfile',
                onClick: onUserProfileClick,
            },
            {
                label: 'Change Password',
                dataTestId: 'userDropdownItemChangePassword',
                onClick: changePassword,
            },
            {
                label: 'Log Out',
                dataTestId: 'userDropdownItemLogOut',
                onClick: handleLogout,
            },
        ],
        [onUserProfileClick, changePassword, handleLogout]
    )

    const userMenuItems: MenuItemConfig[] = React.useMemo(
        () => [...kpcUserMenuItems, ...commonUserMenuItems],
        [kpcUserMenuItems, commonUserMenuItems]
    )

    const onFirmSelect = React.useCallback(() => {
        if (userFirmDetail) {
            // Emit logout event to notify services (e.g., clear preview features cache)
            AuthEventService.emitLogout()

            window.localStorage.clear()
            window.open(userFirmDetail?.firmAccountTypeApplicationURL, '_self')
        }
    }, [userFirmDetail])

    const getUserFirm = React.useCallback((data: CurrentUserFirmsProps) => {
        setUserFirmDetail(data)
    }, [])

    const responsiveMenus = React.useMemo(() => {
        return (
            <ResponsiveMenus
                accountType={
                    currentUserInfoResponse?.currentUserInfo?.account
                        .accountType
                }
                userName={{
                    first: currentUserData?.user?.profileInfo?.firstName,
                    last: currentUserData?.user?.profileInfo?.lastName,
                    userId: currentUserInfoResponse?.currentUserInfo?.kpUserId,
                }}
                userProfileImage={currentUserData?.user?.profileInfo?.imageUrl}
                helpMenuItems={helpMenuItems}
                userMenuItems={userMenuItems}
                variant="kpc"
                buildNumber={processEnv.REACT_APP_BuildNumber}
                showMultiFirm={true}
                onFirmSelect={onFirmSelect}
                getUserFirms={getUserFirm}
                onUserProfileClick={onUserProfileClick}
                pollInterval={parseInt(processEnv.REACT_APP_NotifInterval)}
                notificationFilter={
                    processEnv.REACT_APP_ShowSmartUpdates !== '1'
                        ? (notification: UserNotification) => {
                            // Filter out notification type 13 (Specpoint Family Release)
                            return notification.notificationType !== "13"
                        }
                        : undefined
                }
            />
        )
    }, [
        currentUserInfoResponse,
        helpDeskEmailAddress,
        currentUserData,
        helpMenuItems,
        userMenuItems,
        getUserFirm,
        onFirmSelect,
        onUserProfileClick,
    ])

    const designatedAdminRoutingProps: GetDesignatedAdminRoutingComponentProps | null = React.useMemo(() => {
        if (currentUserInfoResponse && !firmDesignatedAdminLoading) {
            return {
                location: {
                    ...location,
                },
                firmId:
                    currentUserInfoResponse.currentUserInfo.account.accountId,
                kpUserId: currentUserInfoResponse.currentUserInfo.kpUserId,
                getFirmDesignatedAdminUserId,
                firmDesignatedAdminCalled,
                firm: firmDesignatedAdminResponse?.firm,
            }
        }
        return null
    }, [
        firmDesignatedAdminResponse,
        currentUserInfoResponse,
        firmDesignatedAdminCalled,
        firmDesignatedAdminLoading,
        getFirmDesignatedAdminUserId,
        location,
    ])

    const designatedAdminRoutingComponent = React.useMemo(() => {
        if (!designatedAdminRoutingProps) return null

        return getDesignatedAdminRoutingComponent(designatedAdminRoutingProps)
    }, [designatedAdminRoutingProps])

    useEffect(() => {
        if (
            location.pathname === '/account' &&
            !!currentUserInfoResponse &&
            !firmDesignatedAdminCalled
        ) {
            getFirmDesignatedAdminUserId({
                variables: {
                    id:
                        currentUserInfoResponse.currentUserInfo.account
                            ?.accountId,
                },
            })
        }
        if (
            location.pathname.includes('/preview') ||
            location.pathname.includes('/projectnotes') ||
            location.pathname.includes('/dialog')
        ) {
            setHeaderVisibility(false)
        }
    }, [
        location,
        currentUserInfoResponse,
        getFirmDesignatedAdminUserId,
        firmDesignatedAdminCalled,
        setHeaderVisibility,
    ])

    useEffect(() => {
        if (!handleLoadingPageChange) return

        if (!client || !currentUserInfoResponse) {
            handleLoadingPageChange(true)
            return
        }

        // hide any open snackbar on page refresh
        if (history.action === 'POP' && !!hideGlobalSnackbar)
            hideGlobalSnackbar()

        if (!location.search.includes('newuser=1')) {
            handleLoadingPageChange(false)
            return
        }

        // if it includes admin as a role, redirect to /account and open the dialog
        if (
            currentUserInfoResponse?.currentUserInfo.roles.includes(Admin) ??
            false
        ) {
            handleLoadingPageChange(!firmDesignatedAdminResponse)
            if (!!firmDesignatedAdminResponse) onUserProfileClick()
            return
        }

        // if the user is not an admin, open the dialog where they get redirected
        handleLoadingPageChange(false)
        onUserProfileClick()
    }, [
        client,
        currentUserInfoResponse,
        showLoadingScreenPage,
        firmDesignatedAdminResponse,
        onUserProfileClick,
        handleLoadingPageChange,
        hideGlobalSnackbar,
        location,
        history.action,
    ])

    useEffect(() => {
        if (headerVisible) {
            setAppHeaderHeight((past) => (past !== 0 ? past : 50))
        } else {
            setAppHeaderHeight(0)
        }
    }, [headerVisible])

    useEffect(() => {
        if (currentUserInfoResponse) {
            isContentTeam(
                currentUserInfoResponse.currentUserInfo?.account
                    ?.accountType === 'System'
            )
            isOmWorkspaceUser(
                currentUserInfoResponse.currentUserInfo?.account
                    ?.accountType === 'AEFirm'
            )
        }
    }, [currentUserInfoResponse])

    useEffect(() => {
        // When main page loads, we collect the date of user's last activity
        setUserLastActive()
    }, [])

    React.useEffect(() => {
        if (
            !window.name.includes(ProductListings) ||
            !window.name.includes(SectionPreview) ||
            !window.name.includes(ProjectNotes)
        ) {
            window.addEventListener('beforeunload', () => {
                if (
                    !window.name.includes(ProductListings) ||
                    !window.name.includes(SectionPreview) ||
                    !window.name.includes(ProjectNotes)
                ) {
                    return handleRelatedProductsBanner
                }
            })

            return () => {
                window.removeEventListener('beforeunload', () => {
                    if (
                        !window.name.includes(ProductListings) ||
                        !window.name.includes(SectionPreview) ||
                        !window.name.includes(ProjectNotes)
                    ) {
                        return handleRelatedProductsBanner
                    }
                })
            }
        }
    }, [handleRelatedProductsBanner, window])

    return showLoadingScreenPage ? null : location.search.includes(
        'newuser=1'
    ) &&
        currentUserInfoResponse?.currentUserInfo.roles.includes(Admin) &&
        !!designatedAdminRoutingComponent ? (
        designatedAdminRoutingComponent
    ) : (
        <>
            <div
                style={{
                    display: showLoadingScreenPage ? 'none' : 'unset',
                    userSelect: 'none',
                }}
            >
                <AppHeader
                    hideActionBar={!actionBarVisible}
                    middleContent={customContent}
                    rightSideContent={responsiveMenus}
                    customActionBar={customActionBar}
                    onHeightChange={setAppHeaderHeight}
                    pageTitle={currentPageTitle}
                    helpMenuItems={helpMenuItems}
                    userMenuItems={userMenuItems}
                    customPartialNavBarMenuComponent={
                        customPartialNavBarMenuComponent
                    }
                    customMenuItems={customMenuItems}
                    headerVisible={headerVisible}
                />
                <ContentContainer
                    top={appHeaderHeight}
                    disableGutters={!headerVisible}
                >
                    <MainPageRouting
                        userRoles={
                            currentUserInfoResponse?.currentUserInfo?.roles ??
                            []
                        }
                    />
                    <UserProfileUpdate
                        userProfileClicked={userProfileClicked}
                        handleDialogClose={handleDialogClose}
                    />
                </ContentContainer>
                {globalSnackbarPropsData && (
                    <GlobalSnackbar
                        hideGlobalSnackbar={hideGlobalSnackbar}
                        {...globalSnackbarPropsData.globalSnackbarProps}
                    />
                )}
            </div>
        </>
    )
}

export default MainPage
